import React from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

// Define spinner variants using tailwind-variants
const spinner = tv({
  base: 'flex flex-col items-center justify-center',
  variants: {
    size: {
      small: { container: 'w-8 h-8', text: 'text-xs mt-2' },
      medium: { container: 'w-14 h-14', text: 'text-sm mt-3' },
      large: { container: 'w-20 h-20', text: 'text-base mt-4' },
    },
    colorScheme: {
      primary: {
        primary: 'var(--primaryColor500)',
        secondary: 'var(--primaryColor600)',
      },
      blue: {
        primary: 'var(--sematicInfoColor)',
        secondary: 'var(--sematicColor)',
      },
      green: {
        primary: 'var(--sematicSuccessColor)',
        secondary: 'var(--sematicSuccessColor500)',
      },
      red: {
        primary: 'var(--sematicErrorColor)',
        secondary: 'var(--sematicErrorColor500)',
      },
      gray: {
        primary: 'var(--textGray)',
        secondary: 'var(--bgGreyColor)',
      },
      black: {
        primary: 'var(--textBlack)',
        secondary: 'var(--textBlack)',
      },
    },
    overlay: {
      true: 'pointer-events-none z-20',
      false: '',
    },
  },
  defaultVariants: {
    size: 'medium',
    colorScheme: 'primary',
    overlay: false,
  },
});

// Type definition for component props
type FancySpinnerProps = VariantProps<typeof spinner> & {
  text?: string;
  className?: string;
  customColors?: {
    primary?: string;
    secondary?: string;
  };
  textColor?: string;
};

const FancySpinner: React.FC<FancySpinnerProps> = ({
  size = 'medium',
  colorScheme = 'primary',
  overlay = false,
  text,
  className = '',
  customColors,
  textColor = 'var(--textBlack)',
}) => {
  const styles = spinner({ size, colorScheme, overlay, className });

  const containerSize = spinner.variants.size[size].container;
  const textSize = spinner.variants.size[size].text;

  const colors = customColors || spinner.variants.colorScheme[colorScheme];
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;

  // Generate unique IDs for gradients to avoid conflicts when multiple spinners are used
  const uniqueId = React.useMemo(() => `spinner-${Math.random().toString(36).substring(2, 9)}`, []);
  const gradientId1 = `${uniqueId}-gradient1`;
  const gradientId2 = `${uniqueId}-gradient2`;

  return (
    <div className={styles}>
      <div className={`relative ${containerSize} bg-transparent ${overlay ? 'bg-white/50' : ''}`}>
        {/* Outer spinner */}
        <div className='absolute inset-0 animate-spin' style={{ animationDuration: '3s' }}>
          <svg viewBox='0 0 100 100' width='100%' height='100%'>
            <defs>
              <linearGradient id={gradientId1} x1='0%' y1='0%' x2='100%' y2='100%'>
                <stop offset='0%' stopColor={primaryColor} stopOpacity='1' />
                <stop offset='100%' stopColor={secondaryColor} stopOpacity='0.6' />
              </linearGradient>
            </defs>
            <circle
              cx='50'
              cy='50'
              r='45'
              fill='none'
              strokeWidth='8'
              stroke={`url(#${gradientId1})`}
              strokeLinecap='round'
              strokeDasharray='70 200'
              strokeDashoffset='0'
            />
          </svg>
        </div>

        {/* Inner spinner (counter direction) */}
        <div
          className='absolute inset-0 animate-spin'
          style={{ animationDuration: '2s', animationDirection: 'reverse' }}
        >
          <svg viewBox='0 0 100 100' width='80%' height='80%' className='absolute left-[10%] top-[10%]'>
            <defs>
              <linearGradient id={gradientId2} x1='100%' y1='100%' x2='0%' y2='0%'>
                <stop offset='0%' stopColor={secondaryColor} stopOpacity='0.8' />
                <stop offset='100%' stopColor={primaryColor} stopOpacity='0.4' />
              </linearGradient>
            </defs>
            <circle
              cx='50'
              cy='50'
              r='38'
              fill='none'
              strokeWidth='6'
              stroke={`url(#${gradientId2})`}
              strokeLinecap='round'
              strokeDasharray='60 180'
              strokeDashoffset='30'
            />
          </svg>
        </div>

        {/* Center dot */}
        <div
          className='absolute w-[20%] h-[20%] rounded-full left-[40%] top-[40%] animate-pulse'
          style={{
            background: primaryColor,
            animationDuration: '1.5s',
          }}
        />
      </div>

      {text && (
        <span className={`${textSize} text-center font-medium`} style={{ color: textColor }}>
          {text}
        </span>
      )}
    </div>
  );
};

export default FancySpinner;
